import { useEffect, useState } from "react";

import { DataForm, IconButton, Stack, TextButton } from "@introist/react-foundation/v2";
import { Block } from "components/atoms";

import { WorkflowStep } from "services/api/WorkflowApi";
import { JourneyStepV2 } from "services/rpc/RpcProvider";

import { StepEditorAccordion } from "modules/workflows/routes/WorkflowEditor/StepEditorAccordion";

import { prefixForm } from "../../../../../utils/FormUtils";
import { EmailAddressList } from "../EmailAddressList";
import { isArray } from "lodash";
import { EmailSenderSelector } from "../EmailSenderSelector";
import { RecipientList, Recipient } from "./RecipientList";

type EmailV2AddressBlockProps = {
  form: DataForm<WorkflowStep> | DataForm<JourneyStepV2>;
  formKeyPrefix?: string;
  disableCc?: boolean;
  disableBcc?: boolean;
  disableReplyTo?: boolean;
};

const toRecipients = (val: any): Recipient[] => {
  if (!isArray(val)) return [];
  return val.map((item: any) =>
    typeof item === "string" ? { value: item } : item
  );
};

export const EmailV2AddressBlock = ({
  form,
  formKeyPrefix,
  disableBcc = false,
  disableCc = false,
  disableReplyTo = false
}: EmailV2AddressBlockProps) => {
  const [replyToActive, setReplyToActive] = useState(false);
  const [ccActive, setCcActive] = useState(false);
  const [bccActive, setBccActive] = useState(false);
  const [sendAsActive, setSendAsActive] = useState(false);

  const prefixedForm = prefixForm(form, formKeyPrefix);

  useEffect(() => {
    if (prefixedForm.get("replyTo") && prefixedForm.get("replyTo") !== "" && !replyToActive)
      setReplyToActive(true);
    if (toRecipients(prefixedForm.get("cc")).length > 0 && !ccActive) setCcActive(true);
    if (toRecipients(prefixedForm.get("bcc")).length > 0 && !bccActive) setBccActive(true);
    if (prefixedForm.get("sendAs")) setSendAsActive(true);
  }, [prefixedForm, replyToActive, ccActive, bccActive, sendAsActive]);

  const showAccordionError =
    prefixedForm.isError("from") ||
    prefixedForm.isError("replyTo") ||
    prefixedForm.isError("to") ||
    prefixedForm.isError("cc") ||
    prefixedForm.isError("bcc") ||
    prefixedForm.isError("sendAs");

  const from = prefixedForm.get("from");

  return (
    <Block blended compact>
      <StepEditorAccordion
        title="Sender & Receivers"
        error={showAccordionError ? "Invalid fields" : undefined}
      >
        <Stack vertical alignItems="stretch" gap="large">
          <StepEditorAccordion.Row label="From">
            <Stack>
              <EmailSenderSelector
                placeholder={!from ? "Select sender" : undefined}
                value={from}
                onSelect={value => prefixedForm.set("from")(value)}
              />
              {from && (
                <IconButton
                  style={{ marginLeft: "auto" }}
                  icon="crossSmall"
                  onClick={() => prefixedForm.set("from")(null)}
                />
              )}
            </Stack>
          </StepEditorAccordion.Row>

          {sendAsActive && (
            <StepEditorAccordion.Row label="Send as">
              <EmailAddressList
                value={prefixedForm.get("sendAs") ? [prefixedForm.get("sendAs")] : []}
                onChange={val => {
                  prefixedForm.set("sendAs")(val.length > 0 ? val[0] : null);
                }}
                placeholder="Select send as"
                single
              />
            </StepEditorAccordion.Row>
          )}

          {replyToActive && (
            <StepEditorAccordion.Row label="Reply to">
              <EmailAddressList
                value={prefixedForm.get("replyTo") ? [prefixedForm.get("replyTo")] : []}
                onChange={val => {
                  prefixedForm.set("replyTo")(val.length > 0 ? val[0] : null);
                }}
                placeholder="Add reply to"
                single
              />
            </StepEditorAccordion.Row>
          )}

          <StepEditorAccordion.MultiOptionRow label="To">
            <RecipientList
              value={toRecipients(prefixedForm.get("to"))}
              onChange={prefixedForm.set("to")}
              placeholder="Add to"
            />
          </StepEditorAccordion.MultiOptionRow>

          {ccActive && (
            <StepEditorAccordion.MultiOptionRow label="Cc">
              <RecipientList
                value={toRecipients(prefixedForm.get("cc"))}
                onChange={prefixedForm.set("cc")}
                placeholder="Add cc"
              />
            </StepEditorAccordion.MultiOptionRow>
          )}
          {bccActive && (
            <StepEditorAccordion.MultiOptionRow label="Bcc">
              <RecipientList
                value={toRecipients(prefixedForm.get("bcc"))}
                onChange={prefixedForm.set("bcc")}
                placeholder="Add bcc"
              />
            </StepEditorAccordion.MultiOptionRow>
          )}

          <Stack justifyContent="flex-end">
            {!sendAsActive && (
              <TextButton onClick={() => setSendAsActive(true)}>Send as</TextButton>
            )}
            {!replyToActive && !disableReplyTo && (
              <TextButton onClick={() => setReplyToActive(true)}>Reply to</TextButton>
            )}
            {!ccActive && !disableCc && (
              <TextButton onClick={() => setCcActive(true)}>Cc</TextButton>
            )}
            {!bccActive && !disableBcc && (
              <TextButton onClick={() => setBccActive(true)}>Bcc</TextButton>
            )}
          </Stack>
        </Stack>
      </StepEditorAccordion>
    </Block>
  );
};
